import {
  POrderHistoryDto,
  POrderHistoryLineDto,
  POrderHistoryTransactionDto
} from '../../../../types/purchaseOrderHistory';
import { purchaseOrderHistoryService } from '../../../../services/purchaseOrderHistoryService';

/**
 * Export purchase order history to Excel-compatible format
 * Creates a hierarchical flattened view with orders, lines, and transactions
 */

interface ExcelRow {
  // Order columns
  orderNo: number | string;
  orderNoERP: string;
  orderStatus: string;
  orderDate: string;
  deliveryDate: string;
  supplierNo: string;
  supplierName: string;
  warehouse: string;
  totalLines: number | string;
  totalQuantity: number | string;
  totalDelivered: number | string;
  totalTransactions: number | string;

  // Line columns
  lineNo: number | string;
  lineStatus: string;
  itemNo: string;
  itemDescription: string;
  lineQuantity: number | string;
  lineQuantityDelivered: number | string;
  lineRemainingQuantity: number | string;
  itemUnitCode: string;
  stdLocation: string;
  itemBatchNo: string;

  // Transaction columns
  transactionId: number | string;
  transactionType: string;
  transactionStatus: string;
  transactionQuantity: number | string;
  transactionLocation: string;
  transactionToLocation: string;
  transactionDate: string;
  operatorId: string;
  sscc: string;
  serialNo: string;
}

/**
 * Flatten hierarchical order data into rows for Excel
 */
function flattenOrderData(orders: POrderHistoryDto[]): ExcelRow[] {
  const rows: ExcelRow[] = [];

  orders.forEach(order => {
    if (order.lines.length === 0) {
      // Order with no lines
      rows.push(createOrderRow(order));
    } else {
      order.lines.forEach(line => {
        if (line.transactions.length === 0) {
          // Line with no transactions
          rows.push(createLineRow(order, line));
        } else {
          // Line with transactions
          line.transactions.forEach(transaction => {
            rows.push(createTransactionRow(order, line, transaction));
          });
        }
      });
    }
  });

  return rows;
}

function createOrderRow(order: POrderHistoryDto): ExcelRow {
  return {
    orderNo: order.orderNo,
    orderNoERP: order.orderNoERP || '',
    orderStatus: order.orderStatusText,
    orderDate: purchaseOrderHistoryService.formatDate(order.orderDate),
    deliveryDate: purchaseOrderHistoryService.formatDate(order.deliveryDate),
    supplierNo: order.supplierNo || '',
    supplierName: order.supplierName || '',
    warehouse: order.warehouse || '',
    totalLines: order.totalLines,
    totalQuantity: order.totalQuantity,
    totalDelivered: order.totalDelivered,
    totalTransactions: order.totalTransactions,
    lineNo: '',
    lineStatus: '',
    itemNo: '',
    itemDescription: '',
    lineQuantity: '',
    lineQuantityDelivered: '',
    lineRemainingQuantity: '',
    itemUnitCode: '',
    stdLocation: '',
    itemBatchNo: '',
    transactionId: '',
    transactionType: '',
    transactionStatus: '',
    transactionQuantity: '',
    transactionLocation: '',
    transactionToLocation: '',
    transactionDate: '',
    operatorId: '',
    sscc: '',
    serialNo: ''
  };
}

function createLineRow(order: POrderHistoryDto, line: POrderHistoryLineDto): ExcelRow {
  return {
    orderNo: order.orderNo,
    orderNoERP: order.orderNoERP || '',
    orderStatus: order.orderStatusText,
    orderDate: purchaseOrderHistoryService.formatDate(order.orderDate),
    deliveryDate: purchaseOrderHistoryService.formatDate(order.deliveryDate),
    supplierNo: order.supplierNo || '',
    supplierName: order.supplierName || '',
    warehouse: order.warehouse || '',
    totalLines: order.totalLines,
    totalQuantity: order.totalQuantity,
    totalDelivered: order.totalDelivered,
    totalTransactions: order.totalTransactions,
    lineNo: line.lineNo,
    lineStatus: line.lineStatusText,
    itemNo: line.itemNo,
    itemDescription: line.description1 || '',
    lineQuantity: line.quantity || 0,
    lineQuantityDelivered: line.quantityDelivered || 0,
    lineRemainingQuantity: line.remainingQuantity,
    itemUnitCode: line.itemUnitCode || '',
    stdLocation: line.stdLocationName || '',
    itemBatchNo: line.itemBatchNo || '',
    transactionId: '',
    transactionType: '',
    transactionStatus: '',
    transactionQuantity: '',
    transactionLocation: '',
    transactionToLocation: '',
    transactionDate: '',
    operatorId: '',
    sscc: '',
    serialNo: ''
  };
}

function createTransactionRow(
  order: POrderHistoryDto,
  line: POrderHistoryLineDto,
  transaction: POrderHistoryTransactionDto
): ExcelRow {
  return {
    orderNo: order.orderNo,
    orderNoERP: order.orderNoERP || '',
    orderStatus: order.orderStatusText,
    orderDate: purchaseOrderHistoryService.formatDate(order.orderDate),
    deliveryDate: purchaseOrderHistoryService.formatDate(order.deliveryDate),
    supplierNo: order.supplierNo || '',
    supplierName: order.supplierName || '',
    warehouse: order.warehouse || '',
    totalLines: order.totalLines,
    totalQuantity: order.totalQuantity,
    totalDelivered: order.totalDelivered,
    totalTransactions: order.totalTransactions,
    lineNo: line.lineNo,
    lineStatus: line.lineStatusText,
    itemNo: line.itemNo,
    itemDescription: line.description1 || '',
    lineQuantity: line.quantity || 0,
    lineQuantityDelivered: line.quantityDelivered || 0,
    lineRemainingQuantity: line.remainingQuantity,
    itemUnitCode: line.itemUnitCode || '',
    stdLocation: line.stdLocationName || '',
    itemBatchNo: line.itemBatchNo || '',
    transactionId: transaction.id,
    transactionType: transaction.lineTypeText,
    transactionStatus: transaction.lineStatusText,
    transactionQuantity: transaction.quantity || 0,
    transactionLocation: transaction.locationName || '',
    transactionToLocation: transaction.toLocationName || '',
    transactionDate: purchaseOrderHistoryService.formatDateTimeShort(transaction.transDt),
    operatorId: transaction.operatorID || '',
    sscc: transaction.sscc || '',
    serialNo: transaction.itemSerialNo || ''
  };
}

/**
 * Default CSV column headers (Norwegian).
 * The order MUST match the value order built in convertToCSV below.
 * Callers (components) may pass a translated array to localize the export.
 */
export const DEFAULT_EXPORT_HEADERS = [
  'Ordrenr',
  'Ordrenr (ERP)',
  'Ordrestatus',
  'Ordredato',
  'Leveringsdato',
  'Leverandørnr',
  'Leverandør',
  'Lager',
  'Antall linjer',
  'Total mengde',
  'Total levert',
  'Totalt transaksjoner',
  'Linjenr',
  'Linjestatus',
  'Varenr',
  'Varebeskrivelse',
  'Linje mengde',
  'Linje levert',
  'Linje gjenstår',
  'Enhet',
  'Standardlokasjon',
  'Batch nr',
  'Trans ID',
  'Trans type',
  'Trans status',
  'Trans mengde',
  'Fra lokasjon',
  'Til lokasjon',
  'Trans dato',
  'Operatør',
  'SSCC',
  'Serienr'
];

/**
 * Convert rows to CSV format
 */
function convertToCSV(rows: ExcelRow[], headers: string[] = DEFAULT_EXPORT_HEADERS): string {
  if (rows.length === 0) return '';

  // Build CSV content
  const csvRows = [headers.join(';')];

  rows.forEach(row => {
    const values = [
      row.orderNo,
      escapeCsvValue(row.orderNoERP),
      escapeCsvValue(row.orderStatus),
      row.orderDate,
      row.deliveryDate,
      escapeCsvValue(row.supplierNo),
      escapeCsvValue(row.supplierName),
      escapeCsvValue(row.warehouse),
      row.totalLines,
      row.totalQuantity,
      row.totalDelivered,
      row.totalTransactions,
      row.lineNo,
      escapeCsvValue(row.lineStatus),
      escapeCsvValue(row.itemNo),
      escapeCsvValue(row.itemDescription),
      row.lineQuantity,
      row.lineQuantityDelivered,
      row.lineRemainingQuantity,
      escapeCsvValue(row.itemUnitCode),
      escapeCsvValue(row.stdLocation),
      escapeCsvValue(row.itemBatchNo),
      row.transactionId,
      escapeCsvValue(row.transactionType),
      escapeCsvValue(row.transactionStatus),
      row.transactionQuantity,
      escapeCsvValue(row.transactionLocation),
      escapeCsvValue(row.transactionToLocation),
      escapeCsvValue(row.transactionDate),
      escapeCsvValue(row.operatorId),
      escapeCsvValue(row.sscc),
      escapeCsvValue(row.serialNo)
    ];

    csvRows.push(values.join(';'));
  });

  return csvRows.join('\r\n');
}

/**
 * Escape CSV values to handle special characters
 */
function escapeCsvValue(value: string | number): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const stringValue = String(value);

  // If value contains semicolon, comma, newline, or double quote, wrap in quotes
  if (stringValue.includes(';') || stringValue.includes(',') ||
      stringValue.includes('\n') || stringValue.includes('"')) {
    // Escape double quotes by doubling them
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

/**
 * Options for localizing the Excel/CSV export.
 * Components (which have access to the i18n `t` function) can pass these;
 * when omitted, the export falls back to the Norwegian defaults.
 */
export interface ExportToExcelOptions {
  /** Localized column headers (order must match DEFAULT_EXPORT_HEADERS). */
  headers?: string[];
  /** Localized message thrown when there is no data to export. */
  noDataMessage?: string;
}

/**
 * Create and download Excel file (CSV format with Excel compatibility)
 */
export async function exportToExcel(
  orders: POrderHistoryDto[],
  filename: string,
  options?: ExportToExcelOptions
): Promise<void> {
  try {
    // Flatten the hierarchical data
    const rows = flattenOrderData(orders);

    if (rows.length === 0) {
      throw new Error(options?.noDataMessage ?? 'Ingen data å eksportere');
    }

    // Convert to CSV
    const csvContent = convertToCSV(rows, options?.headers);

    // Add BOM for Excel UTF-8 recognition
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;

    // Create blob
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });

    // Create download link
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    // Ensure filename ends with .csv or .xlsx
    const finalFilename = filename.endsWith('.xlsx')
      ? filename.replace('.xlsx', '.csv')
      : filename.endsWith('.csv')
      ? filename
      : `${filename}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', finalFilename);
    link.style.visibility = 'hidden';

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Cleanup
    URL.revokeObjectURL(url);

  } catch (error) {
    console.error('Export error:', error);
    throw error;
  }
}

/**
 * Get summary statistics for export
 */
export function getExportSummary(orders: POrderHistoryDto[]): {
  totalOrders: number;
  totalLines: number;
  totalTransactions: number;
  estimatedRows: number;
} {
  let totalLines = 0;
  let totalTransactions = 0;

  orders.forEach(order => {
    totalLines += order.lines.length;
    order.lines.forEach(line => {
      totalTransactions += line.transactions.length;
    });
  });

  // Estimate rows (each transaction = 1 row, or 1 row per line if no transactions, or 1 row per order if no lines)
  let estimatedRows = 0;
  orders.forEach(order => {
    if (order.lines.length === 0) {
      estimatedRows += 1;
    } else {
      order.lines.forEach(line => {
        if (line.transactions.length === 0) {
          estimatedRows += 1;
        } else {
          estimatedRows += line.transactions.length;
        }
      });
    }
  });

  return {
    totalOrders: orders.length,
    totalLines,
    totalTransactions,
    estimatedRows
  };
}
